"use client";

import { Fragment, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { AlertCircle, ArrowRight, Check } from "lucide-react";
import axios from "axios";

type Step = "email" | "code" | "newPassword";

export default function RecuperacionContrasenaCompleta() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState<Step>("email");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const URL_API = `${process.env.NEXT_PUBLIC_API_URL}/usuario`;

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");
    setEmailError("");

    // Validar correo electrónico
    if (!email) {
      setEmailError("El correo electrónico es obligatorio.");
      setIsLoading(false);
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError("Por favor ingresa un correo electrónico válido.");
      setIsLoading(false);
      return;
    }

    try {
      let resp:any = await axios.post(`${URL_API}/recuperar-password?mail=${email}`)

      if(resp?.data?.mensaje){
        setEmailError(resp?.data?.mensaje)
      }else{
        if(resp?.data?.enviado == false){
          throw new Error("Error enviarndo mail de recuperacion.");
        }
        setStep("code");
        setMessage(
          "Se ha enviado un código de recuperación a tu correo electrónico."
        );
      }
      
    } catch (error) {
      setMessage("Ocurrió un error. Por favor, intenta de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const exec = async () => {
      try {
        const codigo = code.join("");
        const resp:any = await axios.get(`${URL_API}/${email}/codigo/${codigo}`);
        if (resp?.data?.valido == true) {
          setStep("newPassword");
          setMessage("Código correcto. Por favor, ingresa tu nueva contraseña.");
        } else {
          setMessage("Código incorrecto. Por favor, intenta de nuevo.");
        }
      } catch (error) {
        console.log(error);
        setMessage("Código incorrecto. Por favor, intenta de nuevo.");
      }
    }
    exec();
    
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");
    setPasswordError("");
    setConfirmPasswordError("");

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError(
        "Las contraseñas no coinciden. Por favor, inténtalo de nuevo."
      );
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 8 || !/\d/.test(newPassword)) {
      setPasswordError(
        "La contraseña debe tener al menos 8 caracteres y 1 número."
      );
      setIsLoading(false);
      return;
    }

    try {
      const codigo = code.join("");
      await axios.put(`${URL_API}/cambioPassword` , {
        username: email,
        password: newPassword,
        codigo: codigo
      });

      setMessage("Tu contraseña ha sido cambiada exitosamente.");
      resetForm();
    } catch (error) {
      setMessage(
        "Ocurrió un error al cambiar la contraseña. Por favor, intenta de nuevo."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = ()=>{
    setCode([])
    setEmail('')
    setStep('email');
  }

  const handleCodeChange = (index: number, value: string) => {
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Mover al siguiente input si se ingresó un número
    if (value && index < 4) {
      const nextInput = document.getElementById(
        `code-${index + 1}`
      ) as HTMLInputElement;
      if (nextInput) nextInput.focus();
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[92dvh] bg-[#000727] relative z-[999]  ">
      <Card className="w-full max-w-xl shadow-xl relative -top-10">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">
            Recuperar Contraseña
          </CardTitle>
          <CardDescription>
            {step === "email" &&
              "Ingresa tu correo electrónico para recibir un código de recuperación."}
            {step === "code" &&
              "Ingresa el código de 5 dígitos que recibiste por correo electrónico."}
            {step === "newPassword" &&
              "Ingresa y confirma tu nueva contraseña."}
          </CardDescription>
        </CardHeader>
        <form
          onSubmit={
            step === "email"
              ? handleEmailSubmit
              : step === "code"
                ? handleCodeSubmit
                : handleNewPasswordSubmit
          }
        >
          <CardContent>
            <div className="space-y-4">
              {step === "email" && (
                <div className="space-y-2">
                  <Label htmlFor="email">Correo Electrónico</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  {emailError && (
                    <p className="text-sm text-red-600">{emailError}</p>
                  )}
                </div>
              )}
              {step === "code" && (
                <div className="space-y-2">
                  <Label htmlFor="code-0">Código de Recuperación</Label>
                  <div className="flex justify-between items-center">
                    {[0, 1, 2, 3, 4].map((index) => (
                      <Fragment key={index}>

                        <Input
                          id={`code-${index}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          className="w-14 text-center "
                          value={code[index]}
                          onChange={(e) =>
                            handleCodeChange(index, e.target.value)
                          }
                          required
                        />

                        {index !== 4 && <span className="mx-2">-</span>}

                      </Fragment>

                    ))}
                  </div>
                </div>
              )}
              {step === "newPassword" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">Nueva Contraseña</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    {passwordError && (
                      <p className="text-sm text-red-600">{passwordError}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      Confirmar Contraseña
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    {confirmPasswordError && (
                      <p className="text-sm text-red-600">
                        {confirmPasswordError}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col items-start space-y-4">
            <Button
              type="submit"
              className="w-full bg-gradient-to-tr from-blue-700 via-blue-600 to-blue-700 "
              disabled={isLoading}
            >
              {isLoading
                ? "Procesando..."
                : step === "email"
                  ? "Enviar código"
                  : step === "code"
                    ? "Verificar código"
                    : "Cambiar contraseña"}
              {!isLoading &&
                (step === "email" ? (
                  <ArrowRight className="ml-2 h-4 w-4" />
                ) : (
                  <Check className="ml-2 h-4 w-4" />
                ))}
            </Button>
            {message && (
              <div
                className={`flex items-center space-x-2 text-sm ${message.includes("error") || message.includes("incorrecto")
                    ? "text-red-600"
                    : "text-green-600"
                  }`}
              >
                <AlertCircle className="h-4 w-4" />
                <span>{message}</span>
              </div>
            )}
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
